import { forwardRef } from "react";
import {
  Breadcrumbs as MuiBreadcrumbs,
  type BreadcrumbsProps as MuiBreadcrumbsProps,
} from "@mui/material";

import { Typography } from "../Typography";

import type { SxProps, Theme } from "@mui/material/styles";

export interface BreadcrumbsProps extends Omit<
  MuiBreadcrumbsProps,
  "variant" | "color"
> {
  /** Root test id. Default: "ds-breadcrumbs". */
  "data-testid"?: string;
}

/** Items with no color of their own default to this; a child's own color still wins. */
const defaultColorSx: SxProps<Theme> = { color: "text.primary" };

/** Constrains MUI Breadcrumbs to the Centro type scale and token-bound item color. */
export const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(
  (
    {
      separator = <Typography color="textSecondary">/</Typography>,
      maxItems = 8,
      itemsBeforeCollapse = 1,
      itemsAfterCollapse = 1,
      expandText = "Show path",
      component = "nav",
      sx,
      "data-testid": dataTestId = "ds-breadcrumbs",
      ...rest
    },
    ref,
  ) => (
    <MuiBreadcrumbs
      ref={ref}
      data-testid={dataTestId}
      component={component}
      separator={separator}
      maxItems={maxItems}
      itemsBeforeCollapse={itemsBeforeCollapse}
      itemsAfterCollapse={itemsAfterCollapse}
      expandText={expandText}
      sx={[defaultColorSx, sx] as SxProps<Theme>}
      {...rest}
    />
  ),
);

Breadcrumbs.displayName = "Breadcrumbs";
